import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  LogOut, 
  DollarSign, 
  ShoppingCart, 
  Users,
  TrendingUp,
  Eye,
  Edit,
  X,
  ExternalLink,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Link as LinkIcon,
  FileText,
  Paperclip,
  Tag,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Percent
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ORDERS_PER_PAGE = 10;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPayment, setFilterPayment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [deliveredLink, setDeliveredLink] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  
  // Coupon management state
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'coupons'
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [showAddCoupon, setShowAddCoupon] = useState(false);
  const [newCoupon, setNewCoupon] = useState({ code: '', discount_percent: 10, description: '' });
  const [savingCoupon, setSavingCoupon] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchCoupons();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterPayment, filterStatus]);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin');
        return;
      }

      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setOrders(response.data.orders);
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching orders:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        toast.error('Session expired. Please login again.');
        navigate('/admin');
      } else {
        toast.error('Error loading orders');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCoupons = async () => {
    try {
      setLoadingCoupons(true);
      const token = localStorage.getItem('admin_token');
      if (!token) return;

      const response = await axios.get(`${API}/admin/coupons`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCoupons(response.data.coupons || []);
    } catch (err) {
      console.error('Error fetching coupons:', err);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const handleCreateCoupon = async () => {
    if (!newCoupon.code.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    if (newCoupon.discount_percent < 1 || newCoupon.discount_percent > 100) {
      toast.error('Discount must be between 1 and 100');
      return;
    }

    try {
      setSavingCoupon(true);
      const token = localStorage.getItem('admin_token');
      
      await axios.post(`${API}/admin/coupons`, {
        code: newCoupon.code.toUpperCase(),
        discount_percent: parseInt(newCoupon.discount_percent),
        applicable_packages: ['article_three_links'],
        description: newCoupon.description || `${newCoupon.discount_percent}% discount on Article with 3 Links`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Coupon created successfully');
      setShowAddCoupon(false);
      setNewCoupon({ code: '', discount_percent: 10, description: '' });
      fetchCoupons();
    } catch (err) {
      console.error('Error creating coupon:', err);
      toast.error(err.response?.data?.detail || 'Failed to create coupon');
    } finally {
      setSavingCoupon(false);
    }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.patch(`${API}/admin/coupons/${couponId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCoupons(coupons.map(c => 
        c.id === couponId ? { ...c, is_active: response.data.is_active } : c
      ));
      toast.success(response.data.is_active ? 'Coupon activated' : 'Coupon deactivated');
    } catch (err) {
      console.error('Error toggling coupon:', err);
      toast.error('Failed to update coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCoupons(coupons.filter(c => c.id !== couponId));
      toast.success('Coupon deleted');
    } catch (err) {
      console.error('Error deleting coupon:', err);
      toast.error('Failed to delete coupon');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const handleStatusChange = async (orderId, newStatus) => {
    const order = orders.find(o => o.id === orderId);
    
    if (order?.payment_status !== 'paid') {
      toast.error('Cannot update status for unpaid orders');
      return;
    }

    if (newStatus === 'complete' && !order?.delivered_link) {
      toast.error('Please add the delivered link before marking as complete');
      setEditOrder(order);
      setDeliveredLink(order.delivered_link || '');
      return;
    }

    try {
      setUpdatingStatus(orderId);
      const token = localStorage.getItem('admin_token');
      
      await axios.patch(
        `${API}/admin/orders/${orderId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const statusMessages = {
        'processing': 'Order marked as Processing. Customer notified via email.',
        'complete': 'Order completed. Customer notified with delivered link.'
      };
      
      toast.success(statusMessages[newStatus] || `Status updated to ${newStatus}`);
      
      setOrders(orders.map(order => 
        order.id === orderId 
          ? { ...order, order_status: newStatus }
          : order
      ));
      
    } catch (err) {
      console.error('Error updating status:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to update status';
      toast.error(errorMsg);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleSaveDeliveredLink = async () => {
    if (!deliveredLink.trim()) {
      toast.error('Please enter the delivered link URL');
      return;
    }

    try {
      setSavingLink(true);
      const token = localStorage.getItem('admin_token');
      
      await axios.patch(
        `${API}/admin/orders/${editOrder.id}/delivered-link?delivered_link=${encodeURIComponent(deliveredLink)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Delivered link saved successfully');
      
      setOrders(orders.map(order => 
        order.id === editOrder.id 
          ? { ...order, delivered_link: deliveredLink }
          : order
      ));
      
      setEditOrder(null);
      setDeliveredLink('');
      
    } catch (err) {
      console.error('Error saving link:', err);
      toast.error('Failed to save link');
    } finally {
      setSavingLink(false);
    }
  };

  const getPackageName = (type) => {
    const names = {
      'single_link': 'Single Link',
      'article_three_links': 'Article + 3 Links',
      'custom': 'Custom'
    };
    return names[type] || type;
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getShortId = (id) => {
    return id ? `#${id.substring(0, 6).toUpperCase()}` : '';
  };

  const getSubmissionType = (order) => {
    if (order.package_type === 'single_link') {
      return { type: 'Link Insertion', icon: LinkIcon, color: 'text-blue-600 bg-blue-50' };
    } else if (order.package_type === 'article_three_links') {
      if (order.article_file_path) {
        return { type: 'New Post (File)', icon: Paperclip, color: 'text-purple-600 bg-purple-50' };
      } else {
        return { type: 'New Post (Text)', icon: FileText, color: 'text-indigo-600 bg-indigo-50' };
      }
    } else if (order.package_type === 'custom') {
      return { type: 'Custom Request', icon: FileText, color: 'text-orange-600 bg-orange-50' };
    }
    return { type: 'Unknown', icon: FileText, color: 'text-slate-600 bg-slate-50' };
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPayment = filterPayment === 'all' || order.payment_status === filterPayment;
    
    let matchesStatus = filterStatus === 'all';
    if (filterStatus === 'pending') matchesStatus = !order.order_status || order.order_status === 'pending';
    else if (filterStatus === 'processing') matchesStatus = order.order_status === 'processing';
    else if (filterStatus === 'complete') matchesStatus = order.order_status === 'complete';
    
    return matchesSearch && matchesPayment && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / ORDERS_PER_PAGE);
  const startIndex = (currentPage - 1) * ORDERS_PER_PAGE;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + ORDERS_PER_PAGE);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-cyan-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">iT</span>
              </div>
              <h1 className="text-xl font-semibold text-slate-900">Admin Dashboard</h1>
            </div>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Orders</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_orders || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <ShoppingCart className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Paid Orders</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{stats.paid_orders || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Pending</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{stats.pending_orders || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                  <Users className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Revenue</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">${stats.total_revenue?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('orders')}
            variant={activeTab === 'orders' ? 'default' : 'outline'}
            className={activeTab === 'orders' 
              ? 'bg-cyan-600 hover:bg-cyan-700 text-white' 
              : 'bg-white hover:bg-slate-50 text-slate-700'}
            data-testid="tab-orders"
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            Orders
          </Button>
          <Button
            onClick={() => setActiveTab('coupons')}
            variant={activeTab === 'coupons' ? 'default' : 'outline'}
            className={activeTab === 'coupons' 
              ? 'bg-cyan-600 hover:bg-cyan-700 text-white' 
              : 'bg-white hover:bg-slate-50 text-slate-700'}
            data-testid="tab-coupons"
          >
            <Tag className="h-4 w-4 mr-2" />
            Coupons
            {coupons.length > 0 && (
              <Badge className="ml-2 bg-cyan-100 text-cyan-700 hover:bg-cyan-100">{coupons.length}</Badge>
            )}
          </Button>
        </div>

        {/* Coupons Tab */}
        {activeTab === 'coupons' && (
          <Card className="bg-white border-0 shadow-sm mb-6">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                    <Percent className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Coupon Management</CardTitle>
                    <p className="text-sm text-slate-500 mt-0.5">Manage discount coupons for your customers</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowAddCoupon(true)}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid="add-coupon-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Coupon
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {loadingCoupons ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
                </div>
              ) : coupons.length === 0 ? (
                <div className="text-center py-12">
                  <Tag className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No coupons yet</p>
                  <p className="text-sm text-slate-400 mt-1">Create your first coupon to offer discounts to customers</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {coupons.map((coupon) => (
                    <div 
                      key={coupon.id}
                      className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                        coupon.is_active 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : 'bg-slate-50 border-slate-200 opacity-60'
                      }`}
                      data-testid={`coupon-item-${coupon.code}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          coupon.is_active ? 'bg-emerald-100' : 'bg-slate-200'
                        }`}>
                          <span className={`text-lg font-bold ${
                            coupon.is_active ? 'text-emerald-700' : 'text-slate-500'
                          }`}>
                            {coupon.discount_percent}%
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-slate-900">{coupon.code}</span>
                            <Badge className={coupon.is_active 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-slate-200 text-slate-600'
                            }>
                              {coupon.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            {coupon.description || `${coupon.discount_percent}% off Article with 3 Links`}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Used {coupon.usage_count || 0} times
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleCoupon(coupon.id)}
                          className={coupon.is_active 
                            ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50' 
                            : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'}
                          data-testid={`toggle-coupon-${coupon.code}`}
                        >
                          {coupon.is_active ? (
                            <>
                              <ToggleRight className="h-4 w-4 mr-1" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="h-4 w-4 mr-1" />
                              Activate
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCoupon(coupon.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-coupon-${coupon.code}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Orders Tab - Filters & Search */}
        {activeTab === 'orders' && (
          <>
        <Card className="bg-white border-0 shadow-sm mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email or order ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-50 border-slate-200 focus:bg-white"
                />
              </div>
              <div className="flex gap-3">
                <select
                  value={filterPayment}
                  onChange={(e) => setFilterPayment(e.target.value)}
                  className="px-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:bg-white"
                >
                  <option value="all">All Payments</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Unpaid</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="complete">Completed</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders Table */}
        <Card className="bg-white border-0 shadow-sm overflow-hidden">
          <CardHeader className="bg-slate-50 border-b border-slate-100 py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-900">
                Orders
                <span className="ml-2 text-sm font-normal text-slate-500">
                  ({filteredOrders.length} total)
                </span>
              </CardTitle>
              {totalPages > 1 && (
                <span className="text-sm text-slate-500">
                  Page {currentPage} of {totalPages}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/50">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Payment</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedOrders.map((order) => {
                    const submissionType = getSubmissionType(order);
                    const SubmissionIcon = submissionType.icon;
                    return (
                    <tr key={order.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-4 px-4">
                        <span className="text-sm font-mono font-medium text-cyan-600">
                          {getShortId(order.id)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{order.name}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{order.email}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-lg ${submissionType.color}`}>
                            <SubmissionIcon className="h-3.5 w-3.5" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{submissionType.type}</p>
                            <p className="text-xs text-slate-500">{getPackageName(order.package_type)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm font-semibold text-slate-900">
                          ${(order.amount / 100).toFixed(2)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <Badge 
                          className={`text-xs font-medium ${
                            order.payment_status === 'paid' 
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}
                        >
                          {order.payment_status === 'paid' ? 'Paid' : 'Pending'}
                        </Badge>
                      </td>
                      <td className="py-4 px-4">
                        {order.payment_status === 'paid' ? (
                          <select
                            value={order.order_status || 'pending'}
                            onChange={(e) => handleStatusChange(order.id, e.target.value)}
                            disabled={updatingStatus === order.id}
                            className={`px-2 py-1 text-xs font-medium rounded-md border cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                              order.order_status === 'complete' 
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : order.order_status === 'processing'
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : 'bg-slate-50 text-slate-700 border-slate-200'
                            }`}
                          >
                            <option value="pending">Pending</option>
                            <option value="processing">Processing</option>
                            <option value="complete">Completed</option>
                          </select>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium text-slate-400 bg-slate-100 rounded-md">
                            —
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-slate-500">{formatDate(order.timestamp)}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setViewOrder(order)}
                            className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {order.payment_status === 'paid' && (
                            <button
                              onClick={() => {
                                setEditOrder(order);
                                setDeliveredLink(order.delivered_link || '');
                              }}
                              className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                              title="Edit Delivered Link"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>

              {paginatedOrders.length === 0 && (
                <div className="text-center py-16">
                  <ShoppingCart className="h-12 w-12 text-slate-200 mx-auto mb-4" />
                  <p className="text-slate-500 font-medium">No orders found</p>
                  <p className="text-slate-400 text-sm mt-1">Try adjusting your search or filters</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-4 border-t border-slate-100 bg-slate-50/50">
                <p className="text-sm text-slate-500">
                  Showing {startIndex + 1} to {Math.min(startIndex + ORDERS_PER_PAGE, filteredOrders.length)} of {filteredOrders.length} orders
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      if (totalPages <= 7) return true;
                      if (page === 1 || page === totalPages) return true;
                      if (Math.abs(page - currentPage) <= 1) return true;
                      return false;
                    })
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 text-slate-400">...</span>
                        )}
                        <Button
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => goToPage(page)}
                          className={`h-8 w-8 p-0 ${
                            currentPage === page 
                              ? 'bg-cyan-600 text-white hover:bg-cyan-700' 
                              : ''
                          }`}
                        >
                          {page}
                        </Button>
                      </React.Fragment>
                    ))}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}
      </main>

      {/* Add Coupon Modal */}
      {showAddCoupon && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Create New Coupon</h2>
                <p className="text-sm text-slate-500">Add a discount coupon for customers</p>
              </div>
              <button
                onClick={() => {
                  setShowAddCoupon(false);
                  setNewCoupon({ code: '', discount_percent: 10, description: '' });
                }}
                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Coupon Code *</Label>
                <Input
                  placeholder="e.g., SAVE10"
                  value={newCoupon.code}
                  onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                  className="bg-slate-50 border-slate-200 focus:bg-white font-mono uppercase"
                  data-testid="new-coupon-code-input"
                />
                <p className="text-xs text-slate-500 mt-1">Code will be converted to uppercase</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Discount Percentage *</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    placeholder="10"
                    value={newCoupon.discount_percent}
                    onChange={(e) => setNewCoupon({ ...newCoupon, discount_percent: e.target.value })}
                    className="bg-slate-50 border-slate-200 focus:bg-white pr-8"
                    data-testid="new-coupon-discount-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Description (Optional)</Label>
                <Input
                  placeholder="e.g., Holiday special discount"
                  value={newCoupon.description}
                  onChange={(e) => setNewCoupon({ ...newCoupon, description: e.target.value })}
                  className="bg-slate-50 border-slate-200 focus:bg-white"
                  data-testid="new-coupon-description-input"
                />
              </div>
              <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-200">
                <p className="text-sm text-cyan-700">
                  <strong>Note:</strong> This coupon will only apply to the "Article with 3 Links" package.
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => {
                    setShowAddCoupon(false);
                    setNewCoupon({ code: '', discount_percent: 10, description: '' });
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateCoupon}
                  disabled={savingCoupon}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid="create-coupon-btn"
                >
                  {savingCoupon ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    'Create Coupon'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Order Modal */}
      {viewOrder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Order Details</h2>
                <p className="text-sm text-slate-500 font-mono">{getShortId(viewOrder.id)}</p>
              </div>
              <button
                onClick={() => setViewOrder(null)}
                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
              <div className="space-y-6">
                {/* Order Type Banner */}
                {(() => {
                  const subType = getSubmissionType(viewOrder);
                  const SubIcon = subType.icon;
                  return (
                    <div className={`flex items-center gap-3 p-4 rounded-xl ${subType.color}`}>
                      <SubIcon className="h-5 w-5" />
                      <div>
                        <p className="font-semibold">{subType.type}</p>
                        <p className="text-sm opacity-80">{getPackageName(viewOrder.package_type)}</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Customer Info */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Customer</h3>
                  <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                    <p className="font-medium text-slate-900">{viewOrder.name}</p>
                    <p className="text-sm text-slate-600">{viewOrder.email}</p>
                    <p className="text-sm text-slate-600">{viewOrder.phone}</p>
                  </div>
                </div>

                {/* Order Info */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Order Info</h3>
                  <div className="bg-slate-50 rounded-xl p-4 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Amount</p>
                      <p className="font-semibold text-emerald-600">${(viewOrder.amount / 100).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Payment</p>
                      <Badge className={viewOrder.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {viewOrder.payment_status}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Order Status</p>
                      <Badge className={
                        viewOrder.order_status === 'complete' ? 'bg-emerald-100 text-emerald-700' :
                        viewOrder.order_status === 'processing' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {viewOrder.order_status || 'Pending'}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Date</p>
                      <p className="text-sm text-slate-900">{formatDate(viewOrder.timestamp)}</p>
                    </div>
                  </div>
                </div>

                {/* Submission Details - Single Link Package */}
                {viewOrder.package_type === 'single_link' && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                      <LinkIcon className="h-3.5 w-3.5 inline mr-1" />
                      Link Insertion Details
                    </h3>
                    <div className="bg-blue-50 rounded-xl p-4 space-y-3">
                      {viewOrder.itmunch_article && (
                        <div>
                          <p className="text-xs font-medium text-blue-700 mb-1">iTMunch Article (where link will be placed)</p>
                          <a href={viewOrder.itmunch_article} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all flex items-center gap-1">
                            {viewOrder.itmunch_article}
                            <ExternalLink className="h-3 w-3 flex-shrink-0" />
                          </a>
                        </div>
                      )}
                      {viewOrder.url && (
                        <div>
                          <p className="text-xs font-medium text-blue-700 mb-1">Customer's URL (link to insert)</p>
                          <a href={viewOrder.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all flex items-center gap-1">
                            {viewOrder.url}
                            <ExternalLink className="h-3 w-3 flex-shrink-0" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Submission Details - Article with 3 Links Package */}
                {viewOrder.package_type === 'article_three_links' && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                      <FileText className="h-3.5 w-3.5 inline mr-1" />
                      New Post Details
                    </h3>
                    <div className="bg-indigo-50 rounded-xl p-4 space-y-3">
                      {/* Article Content or File */}
                      {viewOrder.article_file_path ? (
                        <div className="bg-purple-100 rounded-lg p-3 flex items-center gap-3">
                          <Paperclip className="h-5 w-5 text-purple-600" />
                          <div>
                            <p className="text-xs font-medium text-purple-700">Attached File</p>
                            <p className="text-sm text-purple-600">{viewOrder.article_file_path.split('/').pop()}</p>
                          </div>
                        </div>
                      ) : viewOrder.article_content ? (
                        <div>
                          <p className="text-xs font-medium text-indigo-700 mb-2">Article Content</p>
                          <div className="bg-white rounded-lg p-3 text-sm text-slate-700 max-h-32 overflow-y-auto border border-indigo-100">
                            {viewOrder.article_content}
                          </div>
                        </div>
                      ) : null}
                      
                      {/* 3 Links */}
                      <div>
                        <p className="text-xs font-medium text-indigo-700 mb-2">Links to Include (3)</p>
                        <div className="space-y-2">
                          {viewOrder.link1 && (
                            <div className="flex items-center gap-2 bg-white rounded-lg p-2 border border-indigo-100">
                              <span className="text-xs font-bold text-indigo-500 bg-indigo-100 rounded px-1.5 py-0.5">1</span>
                              <a href={viewOrder.link1} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline break-all flex-1">{viewOrder.link1}</a>
                            </div>
                          )}
                          {viewOrder.link2 && (
                            <div className="flex items-center gap-2 bg-white rounded-lg p-2 border border-indigo-100">
                              <span className="text-xs font-bold text-indigo-500 bg-indigo-100 rounded px-1.5 py-0.5">2</span>
                              <a href={viewOrder.link2} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline break-all flex-1">{viewOrder.link2}</a>
                            </div>
                          )}
                          {viewOrder.link3 && (
                            <div className="flex items-center gap-2 bg-white rounded-lg p-2 border border-indigo-100">
                              <span className="text-xs font-bold text-indigo-500 bg-indigo-100 rounded px-1.5 py-0.5">3</span>
                              <a href={viewOrder.link3} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline break-all flex-1">{viewOrder.link3}</a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Custom Request */}
                {viewOrder.package_type === 'custom' && viewOrder.custom_request && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Custom Request</h3>
                    <div className="bg-orange-50 rounded-xl p-4">
                      <p className="text-sm text-slate-700">{viewOrder.custom_request}</p>
                    </div>
                  </div>
                )}

                {/* Legacy Links section for backwards compatibility */}
                {viewOrder.package_type !== 'single_link' && viewOrder.package_type !== 'article_three_links' && (viewOrder.url || viewOrder.itmunch_article || viewOrder.link1) && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Submitted Links</h3>
                    <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                      {viewOrder.itmunch_article && (
                        <div>
                          <p className="text-xs text-slate-500">iTMunch Article</p>
                          <a href={viewOrder.itmunch_article} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-600 hover:underline break-all">{viewOrder.itmunch_article}</a>
                        </div>
                      )}
                      {viewOrder.url && (
                        <div>
                          <p className="text-xs text-slate-500">Customer URL</p>
                          <a href={viewOrder.url} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-600 hover:underline break-all">{viewOrder.url}</a>
                        </div>
                      )}
                      {viewOrder.link1 && <p className="text-sm"><span className="text-slate-500">Link 1:</span> <a href={viewOrder.link1} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">{viewOrder.link1}</a></p>}
                      {viewOrder.link2 && <p className="text-sm"><span className="text-slate-500">Link 2:</span> <a href={viewOrder.link2} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">{viewOrder.link2}</a></p>}
                      {viewOrder.link3 && <p className="text-sm"><span className="text-slate-500">Link 3:</span> <a href={viewOrder.link3} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">{viewOrder.link3}</a></p>}
                    </div>
                  </div>
                )}

                {/* Delivered Link */}
                {viewOrder.delivered_link && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Delivered Backlink</h3>
                    <div className="bg-emerald-50 rounded-xl p-4">
                      <a href={viewOrder.delivered_link} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline flex items-center gap-2 font-medium">
                        {viewOrder.delivered_link}
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Delivered Link Modal */}
      {editOrder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Add Delivered Link</h2>
                <p className="text-sm text-slate-500">{editOrder.name}</p>
              </div>
              <button
                onClick={() => {
                  setEditOrder(null);
                  setDeliveredLink('');
                }}
                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Backlink URL</Label>
                <Input
                  placeholder="https://itmunch.com/your-article"
                  value={deliveredLink}
                  onChange={(e) => setDeliveredLink(e.target.value)}
                  className="bg-slate-50 border-slate-200 focus:bg-white"
                />
                <p className="text-xs text-slate-500 mt-2">Enter the live URL where the customer's link was placed</p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setEditOrder(null);
                    setDeliveredLink('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveDeliveredLink}
                  disabled={savingLink}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                >
                  {savingLink ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Save Link'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
